import { Outlet } from "@tanstack/react-router";
import { AuthProvider, useAuthContext } from "@/providers/auth-provider";
import { CresoTextLogo } from "@core/components/logo/creso-logo";
import { Button } from "web-design-system";
import { MobileViewContainer } from "@core/components/layout/mobile-view-container";
import { Suspense } from "react";
import { imageAssetUrl } from "@/utils/url";
import { Dialogs as TransactionDialogs } from "@transactions/components";
import { Dialogs as MandateDialogs } from "@mandate/index";
import { Dialogs as OnboardingDialogs } from "@crm/components/onboarding";
import { Dialogs as PortfolioDialogs } from "@crm/components/portfolio/dialogs";
import { Dialogs as PlanningDialogs } from "@planning/index";
import { CoreDialogs } from "@core/components/dialogs";
import ErrorBoundary from "../error-boundary";
import { AppNavbar } from "./app-navbar";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

export function AppLayout() {
  return (
    <AuthProvider>
      <InvestorLayout>
        <div className="h-dvh relative overflow-hidden w-full flex">
          <AppNavbar />
          <div className="flex-1 p-2 pl-0 overflow-hidden h-full bg-secondary">
            <ErrorBoundary>
              <Suspense fallback={<div className="size-full bg-background" />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </InvestorLayout>
      <TransactionDialogs />
      <MandateDialogs />
      <OnboardingDialogs />
      <PortfolioDialogs />
      <CoreDialogs />
      <PlanningDialogs />
    </AuthProvider>
  );
}

function InvestorLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuthContext();

  const handleDownloadApp = () => {
    if (isIOS) {
      window.open(
        "https://apps.apple.com/in/app/creso-sips-and-mutual-funds/id6745787516"
      );
    } else {
      window.open(
        "https://play.google.com/store/apps/details?id=creso.mobile&hl=en_IN"
      );
    }
  };

  if (!user) return null;

  if (user.user_type === "investor") {
    return (
      <MobileViewContainer>
        <div className="flex px-6 flex-col items-center size-full justify-center">
          <CresoTextLogo />
          <div className="px-10 pt-7 mt-9 justify-between items-center flex relative overflow-hidden bg-slate-200 rounded-3xl flex-col">
            <span className="text-lg font-semibold text-center text-black">
              Track your investments easily
            </span>
            <img
              src={imageAssetUrl("download-app.webp")}
              className="bg-slate-200"
            />
            <div
              style={{
                background: "rgba(241, 245, 249, 0.69)",
                backdropFilter: "blur(6px)",
              }}
              className="flex flex-row p-4 items-center w-full rounded-b-3xl absolute bottom-0 left-0 gap-2"
            >
              <div className="flex flex-row gap-2 items-center">
                <img
                  className="size-9"
                  src={imageAssetUrl("creso-app.jpg")}
                  height={36}
                  width={36}
                />
                <span className="text-sm font-semibold text-black">
                  Creso: SIPs and Mutual Funds
                </span>
              </div>
              <div className="flex flex-shrink-0 flex-row gap-2 justify-end ml-auto">
                <a href="https://play.google.com/store/apps/details?id=creso.mobile&hl=en_IN">
                  <img
                    className="size-9"
                    src={imageAssetUrl("play-store.webp")}
                    height={36}
                    width={36}
                  />
                </a>
                <a href="https://apps.apple.com/in/app/creso-sips-and-mutual-funds/id6745787516">
                  <img
                    className="size-9"
                    src={imageAssetUrl("app-store.webp")}
                    height={36}
                    width={36}
                  />
                </a>
              </div>
            </div>
          </div>
          <Button
            onClick={handleDownloadApp}
            size="lg"
            className="w-full flex-shrink-0 mt-8"
          >
            Download App
          </Button>
        </div>
      </MobileViewContainer>
    );
  }

  return children;
}
